import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { devLogin, embedLogin, fetchMe, type MeResponse } from '../api/auth';
import { setToken, getToken } from '../api/apiFetch';
import { DEFAULT_BOX_PLAN, type BoxPlan, type BoxSettings, type SessionUser } from '@salaries/shared';

const IFRAME_TOKEN_KEY = 'salaries.iframe.accessToken';

interface AuthState {
  status: 'loading' | 'authenticated' | 'unauthenticated' | 'error';
  user: SessionUser | null;
  settings: BoxSettings | null;
  onboardingCompleted: boolean;
  /** Commercial plan (defaults until /me resolves). */
  plan: BoxPlan;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  refetchMe: () => Promise<void>;
  devLogin: () => Promise<void>;
}

/** Authenticated state from a /me response. */
function authedState(me: MeResponse): AuthState {
  return {
    status: 'authenticated',
    user: me.user,
    settings: me.settings,
    onboardingCompleted: me.onboarding_completed,
    plan: me.plan,
    error: null,
  };
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readUrlAccessToken(): string | null {
  // The Arbox parent sometimes appends a trailing analytics param with `?`
  // instead of `&` (…&accessToken=<jwt>?dataLayer=first_time). The second `?`
  // makes URLSearchParams glue that text onto the JWT, breaking signature
  // verification (intermittent 400s on first-load/dataLayer sessions). A JWT
  // never contains `?`, so trim at the first one. Treat the URL token as
  // possibly-mangled by the parent — don't assume it's the last/clean param.
  const raw = new URLSearchParams(window.location.search).get('accessToken');
  return raw ? raw.split('?')[0]! : null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: 'loading',
    user: null,
    settings: null,
    onboardingCompleted: false,
    plan: DEFAULT_BOX_PLAN,
    error: null,
  });

  const refetchMe = useCallback(async () => {
    try {
      const me: MeResponse = await fetchMe();
      setState(authedState(me));
    } catch (err) {
      setState({
        status: 'unauthenticated',
        user: null,
        settings: null,
        onboardingCompleted: false,
        plan: DEFAULT_BOX_PLAN,
        error: (err as Error).message,
      });
    }
  }, []);

  const runDevLogin = useCallback(async () => {
    const result = await devLogin();
    setToken(result.token);
    const me: MeResponse = await fetchMe();
    setState(authedState(me));
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const urlToken = readUrlAccessToken();
      const cachedIframeToken = sessionStorage.getItem(IFRAME_TOKEN_KEY);
      const cachedSession = getToken();

      // User-switch in same tab: Arbox re-mounts the iframe with a different
      // accessToken for User B while we still hold User A's session JWT.
      // Re-handshake if the URL token differs from what we cached.
      // See guidelines 04-arbox-iframe-auth.md §11.
      if (urlToken && cachedIframeToken && urlToken !== cachedIframeToken) {
        setToken(null);
        sessionStorage.removeItem(IFRAME_TOKEN_KEY);
      }

      // If we already hold a valid-looking session JWT, try /me first.
      if (cachedSession && (!urlToken || urlToken === cachedIframeToken)) {
        try {
          const me: MeResponse = await fetchMe();
          if (!cancelled) setState(authedState(me));
          return;
        } catch {
          setToken(null); // session expired/bad — fall through to fresh login
        }
      }

      if (!urlToken) {
        if (!cancelled) {
          setState({
            status: 'unauthenticated',
            user: null,
            settings: null,
            onboardingCompleted: false,
            plan: DEFAULT_BOX_PLAN,
            error: 'missing accessToken (open this app from inside Arbox)',
          });
        }
        return;
      }

      try {
        const result = await embedLogin(urlToken);
        setToken(result.token);
        sessionStorage.setItem(IFRAME_TOKEN_KEY, urlToken);
        // embed-login returns the session + user; fetch settings via /me so the
        // shape stays consistent with refetchMe.
        const me: MeResponse = await fetchMe();
        if (!cancelled) setState(authedState(me));
      } catch (err) {
        if (!cancelled) {
          setState({
            status: 'error',
            user: null,
            settings: null,
            onboardingCompleted: false,
            plan: DEFAULT_BOX_PLAN,
            error: (err as Error).message,
          });
        }
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({ ...state, refetchMe, devLogin: runDevLogin }),
    [state, refetchMe, runDevLogin],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
